import { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Stack,
    CircularProgress,
    Snackbar,
    Alert,
} from "@mui/material";
import { colors } from "../theme/theme";
import { commentsApi } from "../api/client";

const COLUMNS = [
    { id: "NEW", label: "New", color: "#2563EB", description: "Just submitted" },
    { id: "ACKNOWLEDGED", label: "Acknowledged", color: "#F59E0B", description: "Seen and considered" },
    { id: "IN_PROGRESS", label: "In Progress", color: "#7C3AED", description: "Actively worked on" },
    { id: "RESOLVED", label: "Resolved", color: "#0F9D58", description: "Done or replied" },
];

const CommentCard = ({ comment, onDragStart }) => {
    return (
        <Card
            draggable
            onDragStart={(e) => onDragStart(e, comment.id)}
            sx={{
                mb: 1.5,
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
                "&:hover": { borderColor: colors.text },
                transition: "border-color 0.15s",
            }}
        >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <Chip
                        label={comment.toolId || "general"}
                        size="small"
                        sx={{
                            fontFamily: '"JetBrains Mono"',
                            fontSize: 9,
                            height: 18,
                            bgcolor: colors.surfaceAlt,
                        }}
                    />
                    <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: '"JetBrains Mono"', fontSize: 10 }}>
                        #{comment.id}
                    </Typography>
                </Stack>
                <Typography sx={{ fontSize: 13, lineHeight: 1.55, mb: 1.5, color: colors.text }}>
                    {comment.body.length > 200 ? comment.body.slice(0, 200) + "…" : comment.body}
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600 }}>
                        {comment.authorName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: '"JetBrains Mono"', fontSize: 10 }}>
                        {new Date(comment.createdAt).toLocaleDateString()}
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );
};

export const KanbanPage = () => {
    const [board, setBoard] = useState({ NEW: [], ACKNOWLEDGED: [], IN_PROGRESS: [], RESOLVED: [] });
    const [loading, setLoading] = useState(true);
    const [dragOver, setDragOver] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const load = () => {
        setLoading(true);
        commentsApi
            .board()
            .then(setBoard)
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const handleDragStart = (e, id) => {
        e.dataTransfer.setData("text/plain", String(id));
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e, colId) => {
        e.preventDefault();
        setDragOver(colId);
    };

    const handleDrop = async (e, newStatus) => {
        e.preventDefault();
        setDragOver(null);
        const id = Number.parseInt(e.dataTransfer.getData("text/plain"), 10);
        if (!id) return;

        // Find current column and card
        let currentStatus = null;
        let card = null;
        for (const col of COLUMNS) {
            const found = board[col.id]?.find((c) => c.id === id);
            if (found) { currentStatus = col.id; card = found; break; }
        }
        if (!card || currentStatus === newStatus) return;

        // Optimistic update
        setBoard((b) => ({
            ...b,
            [currentStatus]: b[currentStatus].filter((c) => c.id !== id),
            [newStatus]: [{ ...card, status: newStatus }, ...(b[newStatus] || [])],
        }));

        try {
            await commentsApi.updateStatus(id, newStatus);
            setSnackbar({ open: true, message: `Moved to ${newStatus}`, severity: "success" });
        } catch (err) {
            setSnackbar({ open: true, message: "Failed to update — reloading", severity: "error" });
            load();
        }
    };

    return (
        <Box>
            <Box mb={4}>
                <Typography variant="overline" sx={{ color: colors.textMuted }}>
                    Roadmap
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}>
                    Kanban board.
                </Typography>
                <Typography sx={{ color: colors.textMuted, maxWidth: 620 }}>
                    Drag feedback cards across columns to track what's being worked on. Public read-only on production;
                    drag-and-drop is enabled here for triage.
                </Typography>
            </Box>

            {loading ? (
                <Box textAlign="center" py={6}><CircularProgress /></Box>
            ) : (
                <Grid container spacing={2}>
                    {COLUMNS.map((col) => {
                        const cards = board[col.id] || [];
                        return (
                            <Grid item xs={12} sm={6} md={3} key={col.id}>
                                <Box
                                    onDragOver={(e) => handleDragOver(e, col.id)}
                                    onDragLeave={() => setDragOver(null)}
                                    onDrop={(e) => handleDrop(e, col.id)}
                                    sx={{
                                        bgcolor: dragOver === col.id ? colors.accentSoft : colors.surfaceAlt,
                                        border: "2px dashed",
                                        borderColor: dragOver === col.id ? col.color : "transparent",
                                        borderRadius: 2,
                                        p: 2,
                                        minHeight: 500,
                                        transition: "all 0.15s",
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: col.color }} />
                                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: colors.text }}>
                                            {col.label}
                                        </Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Box
                                            sx={{
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: 1,
                                                bgcolor: colors.surface,
                                                fontFamily: '"JetBrains Mono"',
                                                fontSize: 11,
                                                fontWeight: 600,
                                                color: colors.text,
                                            }}
                                        >
                                            {cards.length}
                                        </Box>
                                    </Stack>
                                    <Typography variant="caption" sx={{ color: colors.textMuted, mb: 2, display: "block" }}>
                                        {col.description}
                                    </Typography>
                                    {cards.length === 0 ? (
                                        <Box sx={{
                                            border: `1px dashed ${colors.border}`,
                                            borderRadius: 1.5,
                                            p: 2,
                                            textAlign: "center",
                                            color: colors.textMuted,
                                            fontSize: 12,
                                        }}>
                                            Empty
                                        </Box>
                                    ) : (
                                        cards.map((c) => (
                                            <CommentCard key={c.id} comment={c} onDragStart={handleDragStart} />
                                        ))
                                    )}
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};
